import React, { useState, useEffect, useContext, createRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import MaskedInput from 'react-text-mask';

import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose } from '@/components/common/popover';
import { useAddCreditCard2, useUpdateCreditCard2 } from '@/components/myaccount/hooks/useCreditcard';
import { CartContext } from '@/contexts/CartContext';
import { createPaypalTransaction, ensureReCaptchaReady } from '@/external/service/paypal.service';
import { TmCreditCardType, TmCreditCardSelectionType, TmCreditCard, TmCreditCardRequest } from '@/tscapi/model/account';
import { TmPaymentRequest } from '@/tscapi/model/request/account_cart-request';
import { TmSemafoneConfig } from '@/tscapi/model/semafone-config';
import { getSemafoneConfig } from '@/tscapi/service/semafone.service';
import { AccountUtils } from '@/tscapi/utils/account-utils';
import { getApiErrorMessage } from '@/tscapi/utils/api-error';
import { Storage } from '@/tscapi/utils/browser-storage';
import { Cookie } from '@/tscapi/utils/cookie';
import { FavouriteStorage } from '@/tscapi/utils/favourites-storage';

declare let PCILanguage: any;
declare let PCIResetParams: any;
declare let PCIResetIframe: any;
declare let grecaptcha: any;
declare let paypal: any;

interface CreditCardProps {
    setCheckoutStep: (string) => void;
    selectedCreditCard: TmCreditCard;
    setAddModalIsOpen: (boolean) => void;
    setEditModalIsOpen: (boolean) => void;
    setOpenParentModal: (boolean) => void;
    showPayPal: boolean;
}

const creditCardRef: React.RefObject<HTMLInputElement> = createRef();
export const CreditCard = ({
    setCheckoutStep,
    setAddModalIsOpen,
    setEditModalIsOpen,
    selectedCreditCard,
    setOpenParentModal,
    showPayPal,
}: CreditCardProps) => {
    const { t } = useTranslation();
    const addMutation = useAddCreditCard2();
    const updateMutation = useUpdateCreditCard2();

    const [creditCardType, setCreditCardType] = useState('');
    const {
        getCart,
        updatePaymentMethod,
        submitCreditCard,
        setSubmitCreditCard,
        cartData,
        paypalApprovePayment,
        setIsLoading,
    } = useContext(CartContext);

    const [paymentCardType, setPaymentCardType] = useState<TmCreditCardSelectionType>(TmCreditCardSelectionType.NonTSC);
    const [completeSemafoneResp, setCompleteSemafoneResponse] = useState('');
    const [creditCardExpiryMonth, setCreditCardExpiryMonth] = useState('');
    const [creditCardExpiryYear, setCreditCardExpiryYear] = useState('');
    const [pagePopupLink, setPagePopupLink] = useState('');
    const [creditCardErrorMessage, setCreditCardErrorMessage] = useState('');
    const showSemafoneInput = !selectedCreditCard;
    const [isOpen, setIsOpen] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const [paypalSelected, setPaypalSelected] = useState(false);
    const [paypalPaymentApproved, setPaypalPaymentApproved] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [showPayPalButtonNotClickedMsg, setShowPayPalButtonNotClickedMsg] = useState(false);
    const [cvvEnabled, setCvvEnabled] = useState(window.cvvCheckEnabled);
    const paypalRef = useRef();

    const getPayPalPaymentResponse = async (cartData) => {
        try {
            const result = await createPaypalTransaction(cartData);
            if (result && result.data && result.data.token) {
                return result.data;
            }
        } catch (error) {
            //setErrorMessage(error);
            console.log(error);
        }
    };

    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
    useEffect(() => {
        if (selectedCreditCard) {
            setCreditCardType(selectedCreditCard.Type);

            const expiryMonth =
                selectedCreditCard.DisplayExpirationMonth.length == 1
                    ? '0' + selectedCreditCard.DisplayExpirationMonth
                    : selectedCreditCard.DisplayExpirationMonth;
            setCreditCardExpiryMonth(expiryMonth);

            const expiryYear =
                selectedCreditCard.DisplayExpirationYear.length == 4
                    ? selectedCreditCard.DisplayExpirationYear.slice(2, 4)
                    : selectedCreditCard.DisplayExpirationYear;
            setCreditCardExpiryYear(expiryYear);
        }

        let semafoneConfig: TmSemafoneConfig;
        const getSemafoneData = async () => {
            const response = await getSemafoneConfig();
            semafoneConfig = response.data;
            PCILanguage = 'en_CA';
            PCIResetParams(semafoneConfig);
            PCIResetIframe();
        };
        getSemafoneData();
    }, []);

    const onCardSelectionChanged = (e) => {
        setCreditCardErrorMessage('');
        setShowPayPalButtonNotClickedMsg(false);

        setPaymentCardType(e.target.value as TmCreditCardSelectionType);

        if (showPayPal) setPaypalSelected(false);
    };

    const onPayPalButtonClicked = () => {
        setPaymentCardType(TmCreditCardSelectionType.PayPal);
        setPaypalSelected(true);
    };
    const popperStyle = {};
    let timeOutClear;
    const handleTouchStart = () => {
        setIsOpen(true);
        setIsTouched(true);
    };
    const handleEnter = () => {
        if (!isTouched) {
            clearTimeout(timeOutClear);
            setIsOpen(true);
        }
    };
    const handleLeave = () => {
        if (!isTouched) {
            timeOutClear = setTimeout(() => setIsOpen(false), 400);
        }
    };
    const formatMaskedCardNumber = (type: string, maskedNumber: string) => {
        const compactMasked = (maskedNumber || '').replace(/\s+/g, '');
        const lastFour = compactMasked.slice(-4);
        if (!lastFour) return compactMasked;
        if (type === 'Amex') return `**** ****** *${lastFour}`;
        if (type === 'Visa' || type === 'MasterCard') return `**** **** **** ${lastFour}`;
        return compactMasked.replace(/(.{4})/g, '$1 ').trim();
    };

    const onNonTscCreditCardAdded = (e) => {
        const responseValue = e.target.value;
        setCompleteSemafoneResponse(responseValue);
        try {
            const semafoneResp = JSON.parse(responseValue);
            if (semafoneResp && semafoneResp.SemafoneValidationState == 'SUCCESS' && semafoneResp.Token) {
                setCreditCardType(AccountUtils.getCardTypeFromScheme(semafoneResp.CardScheme));
            }
        } catch {
            // Ignore malformed iframe response until Semafone writes a valid payload.
        }
    };

    const updatePaypalPayment = async () => {
        try {
            await paypalApprovePayment();
            setCheckoutStep('review-order');
        } catch (error) {
            //setErrorMessage(error);
        }
    };

    const dispatchAddPaymentInfoEvent = () => {
        const checkoutPixelEvent = new CustomEvent('addPaymentInfoEvent');
        document.dispatchEvent(checkoutPixelEvent);
    };

    useEffect(() => {
        if (paypalSelected) {
            //disable-card=visa,mastercard,amex,discover,jcb
            if (!loaded) {
                const paypalClientId = window['payPalClientId'];
                const script = document.createElement('script');
                script.src =
                    'https://www.paypal.com/sdk/js?client-id=' +
                    paypalClientId +
                    '&currency=CAD' +
                    // + "&debug=true"
                    '&locale=en_CA&disable-funding=credit,card&commit=false&intent=order';
                script.setAttribute('data-namespace', 'paypal');
                script.addEventListener('load', () => setLoaded(true));
                document.body.appendChild(script);
            }
            if (loaded) {
                const renderPayPal = async () => {
                    paypal
                        .Buttons({
                            style: {
                                layout: 'vertical',
                                color: 'gold',
                                shape: 'rect',
                                label: 'pay',
                            },
                            createOrder: async (data, actions) => {
                                //clear paypal info to reapprove new payment transaction
                                const cookie = new Cookie();
                                cookie.deletePaypalInfo();
                                const paymentResponse = await getPayPalPaymentResponse(cartData);
                                cookie.PaypalPaymentId = paymentResponse.id;
                                return paymentResponse.token;
                            },
                            onApprove: async (data, actions) => {
                                setPaypalPaymentApproved(true);
                            },
                            onCancel: () => {
                                const cookie = new Cookie();
                                cookie.deletePaypalInfo();
                            },
                            onError: (err) => {
                                setError(err);
                                console.error(err);
                            },
                        })
                        .render(paypalRef.current);
                };
                setTimeout(() => {
                    renderPayPal();
                });
            }
        } else {
            if (loaded && paypal) paypal.Buttons({}).close();
        }
    }, [paypalSelected, loaded]);

    useEffect(() => {
        if (paypalPaymentApproved) {
            updatePaypalPayment();
        }
    }, [paypalPaymentApproved]);

    useEffect(() => {
        if (paypalSelected) {
            setShowPayPalButtonNotClickedMsg(true);
            dispatchAddPaymentInfoEvent();
        } else if (submitCreditCard == 'add' || submitCreditCard == 'create') {
            setSubmitCreditCard('none');
            const addCard = async () => {
                setIsLoading(true);
                const creditCard = new TmCreditCardRequest();
                setCreditCardErrorMessage('');
                if (paymentCardType == TmCreditCardSelectionType.NonTSC) {
                    if (completeSemafoneResp) {
                        const semafoneResp = JSON.parse(completeSemafoneResp);
                        if (
                            !creditCardExpiryMonth ||
                            creditCardExpiryMonth.length < 1 ||
                            Number(creditCardExpiryMonth) < 1 ||
                            Number(creditCardExpiryMonth) > 12
                        ) {
                            setCreditCardErrorMessage(t('Please enter valid expiration date.'));
                            setIsLoading(false);
                            return;
                        }
                        if (!creditCardExpiryYear || creditCardExpiryYear.length < 2) {
                            setCreditCardErrorMessage('Please enter valid expiration date.');
                            setIsLoading(false);
                            return;
                        }
                        // check cvv
                        if (cvvEnabled) {
                            if (semafoneResp && semafoneResp.CardScheme == 'AMEX') {
                                if (!pagePopupLink || pagePopupLink.length != 4) {
                                    setCreditCardErrorMessage(
                                        t(
                                            'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                                        ),
                                    );
                                    setIsLoading(false);
                                    return;
                                }
                            }
                            if (
                                (semafoneResp && semafoneResp.CardScheme == 'VISA') ||
                                (semafoneResp && semafoneResp.CardScheme == 'MASTERCARD')
                            ) {
                                if (!pagePopupLink || pagePopupLink.length != 3) {
                                    setCreditCardErrorMessage(
                                        t(
                                            'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                                        ),
                                    );
                                    setIsLoading(false);
                                    return;
                                }
                            }
                        }
                        if (semafoneResp && semafoneResp.SemafoneValidationState == 'SUCCESS' && semafoneResp.Token) {
                            creditCard.Type = AccountUtils.getCardTypeFromScheme(semafoneResp.CardScheme);
                            creditCard.Number = semafoneResp.Token;
                            creditCard.MaskedNumber = semafoneResp.MaskedPan;
                            creditCard.DisplayExpirationMonth =
                                creditCardExpiryMonth.length == 1 ? '0' + creditCardExpiryMonth : creditCardExpiryMonth;
                            creditCard.DisplayExpirationYear = creditCardExpiryYear;
                            if (cvvEnabled) {
                                creditCard.CVV = pagePopupLink;
                            }
                            const cookie = new Cookie();
                            if (cookie.TscClientIP) {
                                creditCard.ClientIP = cookie.TscClientIP;
                            }
                        } else if (semafoneResp.SemafoneValidationState == 'FAIL') {
                            setCreditCardErrorMessage(semafoneResp.SemafoneValidationErrorDesc);
                            setIsLoading(false);
                            return;
                        }
                    }
                }
                if (creditCard.Number && creditCard.Number.length >= 15) {
                    try {
                        await new Promise<void>((resolve, reject) =>
                            grecaptcha.ready(async function () {
                                try {
                                    const token = await grecaptcha.execute(reCaptchaSiteKey, {
                                        action: 'addcreditcardcheckout',
                                    });
                                    creditCard.ReCaptchaToken = token;
                                    const accountResponse = await addMutation.mutateAsync({ creditcard: creditCard });
                                    if (accountResponse && accountResponse.data) {
                                        const paymentReq = new TmPaymentRequest();
                                        paymentReq.RememberCard = true;
                                        paymentReq.MakeDefaultCard = true;
                                        paymentReq.CardType = creditCard.Type;
                                        paymentReq.CardNumber = creditCard.Number;
                                        paymentReq.CardType = creditCard.Type;
                                        paymentReq.CardExpiryMonth = creditCard.DisplayExpirationMonth;
                                        paymentReq.CardExpiryYear = creditCard.DisplayExpirationYear;
                                        await updatePaymentMethod(paymentReq);

                                        dispatchAddPaymentInfoEvent();
                                        if (submitCreditCard == 'create') {
                                            setCheckoutStep('review-order');
                                        } else if (submitCreditCard == 'add') {
                                            setAddModalIsOpen(false);
                                        }
                                    }
                                    resolve();
                                } catch (error) {
                                    reject(error);
                                }
                            }),
                        );
                    } catch (error) {
                        console.log(error);
                        setPagePopupLink('');
                        //increment error counter so after 3 fails it signs user out and sends to homepage
                        setCreditCardErrorMessage(
                            getApiErrorMessage(error, t('Unable to save your credit card. Please try again.')),
                        );
                        setIsLoading(false);
                    }
                } else {
                    setCreditCardErrorMessage(t('Please enter a valid credit card number.'));
                    setIsLoading(false);
                }
            };

            addCard();
        } else if (submitCreditCard == 'edit') {
            setSubmitCreditCard('none');

            let creditCard: TmCreditCardRequest;

            const editCard = async () => {
                setIsLoading(true);
                creditCard = new TmCreditCardRequest();

                creditCard.Id = selectedCreditCard.Id;
                creditCard.IsDefault = selectedCreditCard.IsDefault;

                setCreditCardErrorMessage('');

                // Validate and set expiry unconditionally
                if (
                    !creditCardExpiryMonth ||
                    creditCardExpiryMonth.length < 1 ||
                    Number(creditCardExpiryMonth) < 1 ||
                    Number(creditCardExpiryMonth) > 12
                ) {
                    setCreditCardErrorMessage(t('Please enter valid expiration date.'));
                    setIsLoading(false);
                    return;
                }
                if (!creditCardExpiryYear || creditCardExpiryYear.length < 2) {
                    setCreditCardErrorMessage(t('Please enter valid expiration date.'));
                    setIsLoading(false);
                    return;
                }
                creditCard.DisplayExpirationMonth =
                    creditCardExpiryMonth.length == 1 ? '0' + creditCardExpiryMonth : creditCardExpiryMonth;
                creditCard.DisplayExpirationYear = creditCardExpiryYear;

                if (paymentCardType == TmCreditCardSelectionType.NonTSC) {
                    if (completeSemafoneResp) {
                        const semafoneResp = JSON.parse(completeSemafoneResp);
                        // check cvv
                        if (cvvEnabled) {
                            if (semafoneResp && semafoneResp.CardScheme == 'AMEX') {
                                if (!pagePopupLink || pagePopupLink.length != 4) {
                                    setCreditCardErrorMessage(
                                        t(
                                            'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                                        ),
                                    );
                                    setIsLoading(false);
                                    return;
                                }
                            }
                            if (
                                (semafoneResp && semafoneResp.CardScheme == 'VISA') ||
                                (semafoneResp && semafoneResp.CardScheme == 'MASTERCARD')
                            ) {
                                if (!pagePopupLink || pagePopupLink.length != 3) {
                                    setCreditCardErrorMessage(
                                        t(
                                            'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                                        ),
                                    );
                                    setIsLoading(false);
                                    return;
                                }
                            }
                        }
                        if (semafoneResp && semafoneResp.SemafoneValidationState == 'SUCCESS' && semafoneResp.Token) {
                            creditCard.Type = AccountUtils.getCardTypeFromScheme(semafoneResp.CardScheme);
                            creditCard.Number = semafoneResp.Token;
                            creditCard.MaskedNumber = semafoneResp.MaskedPan;
                            if (cvvEnabled) {
                                creditCard.CVV = pagePopupLink;
                            }
                            const cookie = new Cookie();
                            if (cookie.TscClientIP) {
                                creditCard.ClientIP = cookie.TscClientIP;
                            }
                        } else if (semafoneResp.SemafoneValidationState == 'FAIL') {
                            setCreditCardErrorMessage(semafoneResp.SemafoneValidationErrorDesc);
                            setIsLoading(false);
                            return;
                        }
                    } else {
                        // Editing existing card without re-entering PAN — reuse existing token
                        creditCard.Type = selectedCreditCard.Type;
                        creditCard.Number = selectedCreditCard.Number;
                        creditCard.MaskedNumber = selectedCreditCard.MaskedNumber;
                        if (cvvEnabled) {
                            creditCard.CVV = pagePopupLink;
                        }
                        const cookie = new Cookie();
                        if (cookie.TscClientIP) {
                            creditCard.ClientIP = cookie.TscClientIP;
                        }
                    }
                }
                if (creditCard.Number && creditCard.Number.length >= 15) {
                    try {
                        await new Promise<void>((resolve, reject) =>
                            grecaptcha.ready(async function () {
                                try {
                                    const token = await grecaptcha.execute(reCaptchaSiteKey, {
                                        action: 'updatecreditcardcheckout',
                                    });
                                    creditCard.ReCaptchaToken = token;
                                    const accountResponse = await updateMutation.mutateAsync({
                                        creditcard: creditCard,
                                        Id: creditCard.Id,
                                    });
                                    if (
                                        accountResponse &&
                                        accountResponse.data &&
                                        cartData.CreditCard.Number == selectedCreditCard.Number
                                    ) {
                                        const paymentReq = new TmPaymentRequest();
                                        paymentReq.RememberCard = true;
                                        paymentReq.MakeDefaultCard = true;
                                        paymentReq.CardType = creditCard.Type;
                                        paymentReq.CardNumber = creditCard.Number;
                                        paymentReq.CardType = creditCard.Type;
                                        paymentReq.CardExpiryMonth = creditCard.DisplayExpirationMonth;
                                        paymentReq.CardExpiryYear = creditCard.DisplayExpirationYear;

                                        await updatePaymentMethod(paymentReq);

                                        dispatchAddPaymentInfoEvent();

                                        setEditModalIsOpen(false);
                                        setOpenParentModal(true);
                                    } else {
                                        await getCart();
                                        setIsLoading(false);
                                        setEditModalIsOpen(false);
                                        setOpenParentModal(true);
                                    }
                                } catch (error) {
                                    reject(error);
                                }
                            }),
                        );
                    } catch (error) {
                        console.log(error);
                        setPagePopupLink('');
                        setCreditCardErrorMessage(
                            getApiErrorMessage(error, t('Unable to save your credit card. Please try again.')),
                        );
                        setIsLoading(false);
                    }
                } else {
                    setCreditCardErrorMessage(t('Please enter a valid credit card number.'));
                    setIsLoading(false);
                }
            };
            editCard();
        }
    }, [submitCreditCard]);

    Modal.setAppElement('#expressCheckout');

    return (
        <>
            <div className="card__box--wide">
                <div className="">
                    <div className="card__header">
                        <b>{selectedCreditCard ? t('Edit credit card') : t('Add a credit card')}</b>
                    </div>
                    <div className="card__body">
                        <div className="creditcard__selector-wrap">
                            {!selectedCreditCard && (
                                <div className="creditcard__wrap" style={{ marginBottom: '16px', marginTop: '16px' }}>
                                    <div className="card__logo--wrap">
                                        {showPayPal && (
                                            <div className="creditcard__selector--cc" key="creditcardTop">
                                                <input
                                                    name="paymentMethodRad"
                                                    type="radio"
                                                    id="standardCreditCardId"
                                                    key="creditcardTopInput"
                                                    data-link-type="radio button"
                                                    data-link-title="credit card"
                                                    data-link-position="card type"
                                                    data-component-type="express checkout"
                                                    checked={paymentCardType == TmCreditCardSelectionType.NonTSC}
                                                    onChange={onCardSelectionChanged}
                                                    value={TmCreditCardSelectionType.NonTSC}
                                                    style={{ display: 'none' }}
                                                />
                                                <label
                                                    className="card__label vertical-align-middle"
                                                    key="creditcardTopLabel"
                                                    htmlFor="standardCreditCardId"
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <svg
                                                            className="card__radio--selected"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 541.6 541.6"
                                                        >
                                                            <path
                                                                data-name="svg 1"
                                                                d="M270.8 0a270.9 270.9 0 1 0 .2 541.8A270.9 270.9 0 0 0 270.8 0zm0 522.5c-138.7 0-251.7-113-251.7-251.7s113-251.7 251.7-251.7 251.7 113 251.7 251.7-113 251.7-251.7 251.7z"
                                                            />
                                                            <circle cx="270.8" cy="270.8" r="108.3" />
                                                        </svg>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 541.6 541.6"
                                                            className="card__radio--unselected"
                                                        >
                                                            <path d="M270.8 0C121.3 0 0 121.3 0 270.8s121.3 270.8 270.8 270.8 270.8-121.3 270.8-270.8S420.3 0 270.8 0zm0 522.5c-138.7 0-251.7-113-251.7-251.7s113-251.7 251.7-251.7 251.7 113 251.7 251.7-113 251.7-251.7 251.7z" />
                                                        </svg>
                                                        <span className="card__label--text">Credit Card</span>
                                                        <div
                                                            className="card__logo--inline"
                                                            style={{ marginLeft: '0px', cursor: 'initial' }}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 220.8 141.8"
                                                                className="tagCCImageVisa"
                                                            >
                                                                <path fill="#231F20" d="M0 0h220.8v141.8H0z" />
                                                                <g fill="#FFF">
                                                                    <path d="M7.1 6.8h206.8v18.4H7.1zM7.1 116.5h206.8v18.4H7.1zM89.9 45.6L68.5 96.3H54.6L44 55.8c-.6-2.5-1.2-3.4-3.1-4.5a56.6 56.6 0 0 0-13-4.3l.3-1.5h22.4c2.9 0 5.4 1.9 6.1 5.2L62.3 80 76 45.6h13.9zm54.6 34.1c.1-13.4-18.6-14.1-18.5-20.1 0-1.8 1.8-3.8 5.6-4.2 1.9-.2 7.1-.4 13 2.3l2.3-10.8a37.1 37.1 0 0 0-12.3-2.2c-13 0-22.2 6.9-22.3 16.8-.1 7.3 6.6 11.4 11.5 13.8 5.1 2.5 6.9 4.1 6.8 6.3 0 3.4-4.1 4.9-7.9 5-6.6.1-10.5-1.8-13.5-3.2l-2.4 11.1c3.1 1.4 8.8 2.6 14.7 2.7 13.9-.1 22.9-6.9 23-17.5m34.4 16.6h12.2l-10.7-50.7h-11.2a6 6 0 0 0-5.6 3.7l-19.8 47h13.8l2.7-7.6h16.9l1.7 7.6zm-14.7-18l6.9-19 4 19h-10.9zm-55.5-32.7L97.8 96.3H84.6l10.9-50.7h13.2z" />
                                                                </g>
                                                                <path
                                                                    fill="#FFF"
                                                                    d="M220.8 0H0v141.8h220.8M218.9 139.9H2.1V1.9h216.8v138z"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <div
                                                            className="card__logo--inline"
                                                            style={{ cursor: 'initial' }}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 220.8 141.8"
                                                                className="tagCCImageMC"
                                                            >
                                                                <path fill="#231F20" d="M0 0h220.8v141.8H0z" />
                                                                <path
                                                                    fill="#FFF"
                                                                    d="M68.7 124.5v-7.1c0-2.7-1.7-4.5-4.4-4.5-1.4 0-2.9.4-4 2a4 4 0 0 0-3.7-2c-1.2 0-2.4.4-3.3 1.7v-1.4h-2.5v11.3h2.5v-6.3c0-2 1.1-3 2.8-3 1.6 0 2.5 1.1 2.5 3v6.3h2.5v-6.3c0-2 1.1-3 2.8-3 1.7 0 2.5 1.1 2.5 3v6.3h2.3zm36.7-11.3h-4v-3.4h-2.5v3.4h-2.3v2.2h2.3v5.2c0 2.6 1 4.2 3.9 4.2 1.1 0 2.3-.3 3.1-.9l-.7-2.1c-.7.4-1.5.6-2.2.6-1.2 0-1.6-.8-1.6-1.9v-5.1h4v-2.2zm21-.2c-1.4 0-2.3.7-3 1.7v-1.4H121v11.3h2.5v-6.3c0-1.9.8-2.9 2.4-2.9l1.5.3.8-2.3c-.6-.4-1.3-.4-1.8-.4zm-31.7 1.1c-1.4-.8-3-1.3-4.6-1.2-2.9 0-4.7 1.4-4.7 3.6 0 1.8 1.4 3 3.9 3.3l1.2.2c1.3.2 2 .5 2 1.2 0 .9-.9 1.4-2.6 1.4-1.4 0-2.7-.4-3.8-1.2l-1.2 1.9c1.4 1 3.1 1.6 4.9 1.5 3.3 0 5.2-1.5 5.2-3.7 0-2-1.5-3-3.9-3.4l-1.2-.2c-1.1-.1-1.9-.4-1.9-1.1 0-.8.8-1.3 2.2-1.3 1.4 0 2.8.5 3.5 1l1-2zm65.8-1.1c-1.4 0-2.3.7-3 1.7v-1.4h-2.4v11.3h2.5v-6.3c0-1.9.8-2.9 2.4-2.9l1.5.3.8-2.3c-.6-.4-1.3-.4-1.8-.4zm-31.6 5.9c0 3.4 2.4 5.9 6 5.9 1.7 0 2.8-.4 4.1-1.3l-1.2-2c-.9.7-1.9 1-3 1-2 0-3.4-1.4-3.4-3.6s1.4-3.6 3.4-3.6c1.1 0 2 .4 3 1l1.2-2a6 6 0 0 0-4.1-1.3c-3.6 0-6 2.5-6 5.9zm23 0v-5.6h-2.5v1.4c-.8-1-2-1.7-3.6-1.7-3.2 0-5.6 2.5-5.6 5.9 0 3.5 2.5 5.9 5.6 5.9 1.6 0 2.8-.6 3.6-1.7v1.4h2.5v-5.6zm-9.2 0c0-2 1.3-3.6 3.4-3.6 2 0 3.4 1.6 3.4 3.6 0 2.1-1.4 3.6-3.4 3.6-2.1 0-3.4-1.6-3.4-3.6zm-29.6-5.9c-3.3 0-5.6 2.4-5.6 5.9 0 3.6 2.4 5.9 5.8 5.9 1.7 0 3.3-.4 4.6-1.6l-1.2-1.8c-.9.8-2.2 1.2-3.3 1.2-1.6 0-3-.7-3.4-2.8h8.4v-.9c0-3.5-2.2-5.9-5.3-5.9zm-.1 2.2c1.6 0 2.6 1 2.9 2.7H110c.3-1.6 1.3-2.7 3-2.7zm61.7 3.7v-10.2h-2.5v5.9c-.8-1-2-1.7-3.6-1.7-3.2 0-5.6 2.5-5.6 5.9 0 3.5 2.5 5.9 5.6 5.9 1.6 0 2.8-.6 3.6-1.7v1.4h2.5v-5.5zm-9.2 0c0-2 1.3-3.6 3.4-3.6 2 0 3.4 1.6 3.4 3.6 0 2.1-1.4 3.6-3.4 3.6-2.1 0-3.4-1.6-3.4-3.6zm-82.9 0v-5.6h-2.5v1.4c-.8-1-2-1.7-3.6-1.7-3.2 0-5.6 2.5-5.6 5.9 0 3.5 2.5 5.9 5.6 5.9 1.6 0 2.8-.6 3.6-1.7v1.4h2.5v-5.6zm-9.2 0c0-2 1.3-3.6 3.4-3.6 2 0 3.4 1.6 3.4 3.6 0 2.1-1.4 3.6-3.4 3.6-2.1 0-3.4-1.6-3.4-3.6zm105.4 4l.4.1.4.2.2.4.1.4-.1.4-.2.4-.4.2-.4.1-.5-.1-.4-.2-.2-.4-.1-.4.1-.4.2-.4.4-.2.5-.1zm0 2l.3-.1.3-.2.2-.3.1-.3-.1-.3-.2-.3-.3-.2-.3-.1-.3.1-.3.2-.2.3-.1.3.1.3.2.3.3.2.3.1zm0-1.4l.3.1.1.2-.1.2-.2.1.3.4h-.2l-.3-.4h-.1v.4h-.2v-1h.4zm-.2.2v.3h.4l.1-.1-.1-.1-.4-.1zM177.3 87v-1.4h.6v-.3h-1.4v.3h.6V87h.2zm2.7 0v-1.7h-.4l-.5 1.1-.5-1.1h-.4V87h.3v-1.2l.5 1.1h.3l.5-1.1V87h.2zm-71.3 8.2l1 .9a42.4 42.4 0 1 1 0-70.8l-1 .9a45.9 45.9 0 0 0 0 69zm30.1-77.1c-8.4 0-16.5 2.4-23.5 7.1l1 .9a46 46 0 0 1 0 69.2l-1 .9a42.5 42.5 0 1 0 23.5-78.1zm-26.3 9.1l-1.6 1.3a42.3 42.3 0 0 0 0 64.2l1.6 1.3 1.6-1.3a42.3 42.3 0 0 0 0-64.2l-1.6-1.3"
                                                                />
                                                                <path
                                                                    fill="#FFF"
                                                                    d="M220.8 0H0v141.8h220.8M218.9 139.9H2.1V1.9h216.8v138z"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <div
                                                            className="card__logo--inline"
                                                            style={{ cursor: 'initial' }}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 220.8 141.8"
                                                                className="tagCCImageAmex"
                                                            >
                                                                <path fill="#FFF" d="M0 0h220.8v141.8H0z" />
                                                                <path d="M138.8 108.4l-4.3-5.3v5.3h-14.2v-9.1c-1.8.8-3.8 1.3-6 1.3h-5v7.8H87.8l-3.9-5.1-3.9 5.1H44.4V74.5h35.8l3.9 5.1 3.9-5.1h130.3V1.9H2.4V49l8.1-19h14L29 40.6V30h17.5l2.7 7.7 2.7-7.7H130v3.9c2.8-2.5 6.7-3.9 11.2-3.9h24.9l4.5 10.6V30h14.7l3.9 6v-6h14.7v33.9h-14.6l-3.9-6.1v6.1H164l-2.1-5.2H156l-2.1 5.2h-13.3c-4.3 0-8-1.3-10.7-3.7v3.7H108l-4.3-5.3v5.3H22.4l-2.1-5.2h-5.9l-2.1 5.2h-10v75.9h215.9v-34c-2.2 1.6-5 2.5-8.2 2.5h-71.2v.1z" />
                                                                <path d="M199.6 59.7V34.2h-6.4V50L183 34.2h-8.2v25.4h6.5V43.3l10.5 16.4h7.8zm-32.7 0h7.3l-10.9-25.4h-8.4L144 59.7h7.1l2.1-5.2h11.6l2.1 5.2zM162.5 49h-7.1l3.5-8.7 3.6 8.7zm-20.8-8.9h6.8v-5.9h-7.3c-8 0-12.3 5.3-12.3 12.5v.4c0 7.8 4.5 12.5 11.8 12.5h.5l2.6-5.7h-2.1c-4.1 0-6.5-2.5-6.5-6.8v-.4c.1-3.7 2.3-6.6 6.5-6.6zm-22.4 19.6h6.5V34.2h-6.5v25.5zm-1 0l-8.2-9.3c4.2-.9 6.7-4 6.7-8 0-4.9-3.4-8.2-8.8-8.2H93.7v25.4h6.5v-9h2.5l7.6 9h8v.1zm-11.1-14.6h-7v-5.2h7c2 0 3 1.2 3 2.6 0 1.5-1 2.6-3 2.6zM69.3 59.7H90V54H75.8v-4.3h13.9v-5.6H75.8V40h14.3v-5.7H69.3v25.4zm-10.6 0H65V34.2H54.9l-5.7 16.2-5.7-16.2H33.2v25.4h6.4V40.9l6.7 18.8H52l6.7-18.8v18.8zm-33.4 0h7.3L21.7 34.2h-8.4L2.4 59.7h7.1l2.1-5.2h11.6l2.1 5.2zM20.9 49h-7.1l3.5-8.7 3.6 8.7zM215.9 84.4l2.5-5.7H205c-5.5 0-8.8 3.4-8.8 8 0 4.8 3.1 7.6 7.9 7.6h6c1.3 0 2.3.8 2.3 2.1s-1 2.1-2.3 2.1h-13.2v5.7H210c5.5 0 8.8-3.3 8.8-8.3 0-4.6-2.9-7.3-7.9-7.3h-6c-1.3 0-2.3-.8-2.3-2.1s1-2.1 2.3-2.1h11zm-36.6 2.1c0-1.3.9-2.1 2.3-2.1h11l2.5-5.7h-13.4c-5.5 0-8.8 3.4-8.8 8 0 4.8 3.1 7.6 7.9 7.6h6c1.3 0 2.3.8 2.3 2.1s-1 2.1-2.3 2.1h-13.2v5.7h13.1c5.5 0 8.8-3.3 8.8-8.3 0-4.6-2.9-7.3-7.9-7.3h-6c-1.3 0-2.3-.8-2.3-2.1zM150 104.1h20.7v-5.7h-14.3v-4.3h13.9v-5.6h-13.9v-4.1h14.3v-5.7H150v25.4zm-1 0l-8.2-9.3c4.2-.9 6.7-4 6.7-8 0-4.9-3.4-8.2-8.8-8.2h-14.1V104h6.5v-9h2.5l7.6 9h7.8v.1zm-11.1-14.5h-7v-5.2h7c2 0 3 1.2 3 2.6 0 1.5-1 2.6-3 2.6zm-16.2-2.3c0-5.1-3.4-8.6-8.8-8.6H98.6v25.4h6.5v-8.3h7.7c5.4.1 8.9-3.6 8.9-8.5zm-6.5 0c0 1.6-1 2.8-3 2.8H105v-5.7h7.2c2 0 3 1.3 3 2.9zM98 104.1L87.8 91.3 98 78.7h-8l-6 7.8-6-7.8h-8.1l10.2 12.8L70 104.1h7.9l6-7.9 6 7.9H98zm-49.3 0h20.7v-5.7H55.2v-4.3h13.9v-5.6H55.2v-4.1h14.3v-5.7H48.7v25.4z" />
                                                                <path
                                                                    fill="#FFF"
                                                                    d="M220.8 0H0v141.8h220.8M218.9 139.9H2.1V1.9h216.8v138z"
                                                                />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {creditCardErrorMessage && (
                                <div className="creditcard__error-wrap">
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            className="form__error-svg"
                                        >
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                        </svg>
                                        {creditCardErrorMessage}
                                    </>
                                </div>
                            )}
                            <div
                                className="standardCCBlock"
                                style={paypalSelected ? { display: 'none' } : { display: 'block' }}
                            >
                                <span style={{ display: 'block' }}>
                                    <h3 className="semafone__cardnumber">{t('Card Number')}</h3>
                                </span>

                                <div>
                                    <div id="CCNumberSemafoneInput">
                                        {selectedCreditCard && !showSemafoneInput && (
                                            <div
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '4px 8px',
                                                    background: '#e3e3e3',
                                                    userSelect: 'none',
                                                    whiteSpace: 'nowrap',
                                                    fontVariantNumeric: 'tabular-nums',
                                                    color: '#4a4a4a',
                                                    width: '66%',
                                                    maxWidth: '260px',
                                                    marginBottom: '14px',
                                                }}
                                            >
                                                {formatMaskedCardNumber(
                                                    creditCardType || selectedCreditCard.Type,
                                                    selectedCreditCard.MaskedNumber || '',
                                                )}
                                            </div>
                                        )}
                                        <div
                                            className="semafoneCapturePage"
                                            id="semafoneCheckoutCapturePage"
                                            style={showSemafoneInput ? { display: 'inline' } : { display: 'none' }}
                                        ></div>
                                        {creditCardType && (
                                            <div className="card__verify-wrap">
                                                <div className="card__logo--verify" style={{ marginLeft: '20px' }}>
                                                    {creditCardType == TmCreditCardType.Visa && (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 220.8 141.8"
                                                            className="tagCCImageVisa"
                                                        >
                                                            <path fill="#231F20" d="M0 0h220.8v141.8H0z" />
                                                            <g fill="#FFF">
                                                                <path d="M7.1 6.8h206.8v18.4H7.1zM7.1 116.5h206.8v18.4H7.1zM89.9 45.6L68.5 96.3H54.6L44 55.8c-.6-2.5-1.2-3.4-3.1-4.5a56.6 56.6 0 0 0-13-4.3l.3-1.5h22.4c2.9 0 5.4 1.9 6.1 5.2L62.3 80 76 45.6h13.9zm54.6 34.1c.1-13.4-18.6-14.1-18.5-20.1 0-1.8 1.8-3.8 5.6-4.2 1.9-.2 7.1-.4 13 2.3l2.3-10.8a37.1 37.1 0 0 0-12.3-2.2c-13 0-22.2 6.9-22.3 16.8-.1 7.3 6.6 11.4 11.5 13.8 5.1 2.5 6.9 4.1 6.8 6.3 0 3.4-4.1 4.9-7.9 5-6.6.1-10.5-1.8-13.5-3.2l-2.4 11.1c3.1 1.4 8.8 2.6 14.7 2.7 13.9-.1 22.9-6.9 23-17.5m34.4 16.6h12.2l-10.7-50.7h-11.2a6 6 0 0 0-5.6 3.7l-19.8 47h13.8l2.7-7.6h16.9l1.7 7.6zm-14.7-18l6.9-19 4 19h-10.9zm-55.5-32.7L97.8 96.3H84.6l10.9-50.7h13.2z" />
                                                            </g>
                                                            <path
                                                                fill="#FFF"
                                                                d="M220.8 0H0v141.8h220.8M218.9 139.9H2.1V1.9h216.8v138z"
                                                            />
                                                        </svg>
                                                    )}
                                                    {creditCardType == TmCreditCardType.MasterCard && (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 220.8 141.8"
                                                            className="tagCCImageMC"
                                                        >
                                                            <path fill="#231F20" d="M0 0h220.8v141.8H0z" />
                                                            <path
                                                                fill="#FFF"
                                                                d="M68.7 124.5v-7.1c0-2.7-1.7-4.5-4.4-4.5-1.4 0-2.9.4-4 2a4 4 0 0 0-3.7-2c-1.2 0-2.4.4-3.3 1.7v-1.4h-2.5v11.3h2.5v-6.3c0-2 1.1-3 2.8-3 1.6 0 2.5 1.1 2.5 3v6.3h2.5v-6.3c0-2 1.1-3 2.8-3 1.7 0 2.5 1.1 2.5 3v6.3h2.3zm36.7-11.3h-4v-3.4h-2.5v3.4h-2.3v2.2h2.3v5.2c0 2.6 1 4.2 3.9 4.2 1.1 0 2.3-.3 3.1-.9l-.7-2.1c-.7.4-1.5.6-2.2.6-1.2 0-1.6-.8-1.6-1.9v-5.1h4v-2.2zm21-.2c-1.4 0-2.3.7-3 1.7v-1.4H121v11.3h2.5v-6.3c0-1.9.8-2.9 2.4-2.9l1.5.3.8-2.3c-.6-.4-1.3-.4-1.8-.4zm-31.7 1.1c-1.4-.8-3-1.3-4.6-1.2-2.9 0-4.7 1.4-4.7 3.6 0 1.8 1.4 3 3.9 3.3l1.2.2c1.3.2 2 .5 2 1.2 0 .9-.9 1.4-2.6 1.4-1.4 0-2.7-.4-3.8-1.2l-1.2 1.9c1.4 1 3.1 1.6 4.9 1.5 3.3 0 5.2-1.5 5.2-3.7 0-2-1.5-3-3.9-3.4l-1.2-.2c-1.1-.1-1.9-.4-1.9-1.1 0-.8.8-1.3 2.2-1.3 1.4 0 2.8.5 3.5 1l1-2zm65.8-1.1c-1.4 0-2.3.7-3 1.7v-1.4h-2.4v11.3h2.5v-6.3c0-1.9.8-2.9 2.4-2.9l1.5.3.8-2.3c-.6-.4-1.3-.4-1.8-.4zm-31.6 5.9c0 3.4 2.4 5.9 6 5.9 1.7 0 2.8-.4 4.1-1.3l-1.2-2c-.9.7-1.9 1-3 1-2 0-3.4-1.4-3.4-3.6s1.4-3.6 3.4-3.6c1.1 0 2 .4 3 1l1.2-2a6 6 0 0 0-4.1-1.3c-3.6 0-6 2.5-6 5.9zm23 0v-5.6h-2.5v1.4c-.8-1-2-1.7-3.6-1.7-3.2 0-5.6 2.5-5.6 5.9 0 3.5 2.5 5.9 5.6 5.9 1.6 0 2.8-.6 3.6-1.7v1.4h2.5v-5.6zm-9.2 0c0-2 1.3-3.6 3.4-3.6 2 0 3.4 1.6 3.4 3.6 0 2.1-1.4 3.6-3.4 3.6-2.1 0-3.4-1.6-3.4-3.6zm-29.6-5.9c-3.3 0-5.6 2.4-5.6 5.9 0 3.6 2.4 5.9 5.8 5.9 1.7 0 3.3-.4 4.6-1.6l-1.2-1.8c-.9.8-2.2 1.2-3.3 1.2-1.6 0-3-.7-3.4-2.8h8.4v-.9c0-3.5-2.2-5.9-5.3-5.9zm-.1 2.2c1.6 0 2.6 1 2.9 2.7H110c.3-1.6 1.3-2.7 3-2.7zm61.7 3.7v-10.2h-2.5v5.9c-.8-1-2-1.7-3.6-1.7-3.2 0-5.6 2.5-5.6 5.9 0 3.5 2.5 5.9 5.6 5.9 1.6 0 2.8-.6 3.6-1.7v1.4h2.5v-5.5zm-9.2 0c0-2 1.3-3.6 3.4-3.6 2 0 3.4 1.6 3.4 3.6 0 2.1-1.4 3.6-3.4 3.6-2.1 0-3.4-1.6-3.4-3.6zm-82.9 0v-5.6h-2.5v1.4c-.8-1-2-1.7-3.6-1.7-3.2 0-5.6 2.5-5.6 5.9 0 3.5 2.5 5.9 5.6 5.9 1.6 0 2.8-.6 3.6-1.7v1.4h2.5v-5.6zm-9.2 0c0-2 1.3-3.6 3.4-3.6 2 0 3.4 1.6 3.4 3.6 0 2.1-1.4 3.6-3.4 3.6-2.1 0-3.4-1.6-3.4-3.6zm105.4 4l.4.1.4.2.2.4.1.4-.1.4-.2.4-.4.2-.4.1-.5-.1-.4-.2-.2-.4-.1-.4.1-.4.2-.4.4-.2.5-.1zm0 2l.3-.1.3-.2.2-.3.1-.3-.1-.3-.2-.3-.3-.2-.3-.1-.3.1-.3.2-.2.3-.1.3.1.3.2.3.3.2.3.1zm0-1.4l.3.1.1.2-.1.2-.2.1.3.4h-.2l-.3-.4h-.1v.4h-.2v-1h.4zm-.2.2v.3h.4l.1-.1-.1-.1-.4-.1zM177.3 87v-1.4h.6v-.3h-1.4v.3h.6V87h.2zm2.7 0v-1.7h-.4l-.5 1.1-.5-1.1h-.4V87h.3v-1.2l.5 1.1h.3l.5-1.1V87h.2zm-71.3 8.2l1 .9a42.4 42.4 0 1 1 0-70.8l-1 .9a45.9 45.9 0 0 0 0 69zm30.1-77.1c-8.4 0-16.5 2.4-23.5 7.1l1 .9a46 46 0 0 1 0 69.2l-1 .9a42.5 42.5 0 1 0 23.5-78.1zm-26.3 9.1l-1.6 1.3a42.3 42.3 0 0 0 0 64.2l1.6 1.3 1.6-1.3a42.3 42.3 0 0 0 0-64.2l-1.6-1.3"
                                                            />
                                                            <path
                                                                fill="#FFF"
                                                                d="M220.8 0H0v141.8h220.8M218.9 139.9H2.1V1.9h216.8v138z"
                                                            />
                                                        </svg>
                                                    )}
                                                    {creditCardType == TmCreditCardType.Amex && (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 220.8 141.8"
                                                            className="paymentmethod__card-svg tagCCImageAmex"
                                                        >
                                                            <path fill="#FFF" d="M0 0h220.8v141.8H0z" />
                                                            <path d="M138.8 108.4l-4.3-5.3v5.3h-14.2v-9.1c-1.8.8-3.8 1.3-6 1.3h-5v7.8H87.8l-3.9-5.1-3.9 5.1H44.4V74.5h35.8l3.9 5.1 3.9-5.1h130.3V1.9H2.4V49l8.1-19h14L29 40.6V30h17.5l2.7 7.7 2.7-7.7H130v3.9c2.8-2.5 6.7-3.9 11.2-3.9h24.9l4.5 10.6V30h14.7l3.9 6v-6h14.7v33.9h-14.6l-3.9-6.1v6.1H164l-2.1-5.2H156l-2.1 5.2h-13.3c-4.3 0-8-1.3-10.7-3.7v3.7H108l-4.3-5.3v5.3H22.4l-2.1-5.2h-5.9l-2.1 5.2h-10v75.9h215.9v-34c-2.2 1.6-5 2.5-8.2 2.5h-71.2v.1z" />
                                                            <path d="M199.6 59.7V34.2h-6.4V50L183 34.2h-8.2v25.4h6.5V43.3l10.5 16.4h7.8zm-32.7 0h7.3l-10.9-25.4h-8.4L144 59.7h7.1l2.1-5.2h11.6l2.1 5.2zM162.5 49h-7.1l3.5-8.7 3.6 8.7zm-20.8-8.9h6.8v-5.9h-7.3c-8 0-12.3 5.3-12.3 12.5v.4c0 7.8 4.5 12.5 11.8 12.5h.5l2.6-5.7h-2.1c-4.1 0-6.5-2.5-6.5-6.8v-.4c.1-3.7 2.3-6.6 6.5-6.6zm-22.4 19.6h6.5V34.2h-6.5v25.5zm-1 0l-8.2-9.3c4.2-.9 6.7-4 6.7-8 0-4.9-3.4-8.2-8.8-8.2H93.7v25.4h6.5v-9h2.5l7.6 9h8v.1zm-11.1-14.6h-7v-5.2h7c2 0 3 1.2 3 2.6 0 1.5-1 2.6-3 2.6zM69.3 59.7H90V54H75.8v-4.3h13.9v-5.6H75.8V40h14.3v-5.7H69.3v25.4zm-10.6 0H65V34.2H54.9l-5.7 16.2-5.7-16.2H33.2v25.4h6.4V40.9l6.7 18.8H52l6.7-18.8v18.8zm-33.4 0h7.3L21.7 34.2h-8.4L2.4 59.7h7.1l2.1-5.2h11.6l2.1 5.2zM20.9 49h-7.1l3.5-8.7 3.6 8.7zM215.9 84.4l2.5-5.7H205c-5.5 0-8.8 3.4-8.8 8 0 4.8 3.1 7.6 7.9 7.6h6c1.3 0 2.3.8 2.3 2.1s-1 2.1-2.3 2.1h-13.2v5.7H210c5.5 0 8.8-3.3 8.8-8.3 0-4.6-2.9-7.3-7.9-7.3h-6c-1.3 0-2.3-.8-2.3-2.1s1-2.1 2.3-2.1h11zm-36.6 2.1c0-1.3.9-2.1 2.3-2.1h11l2.5-5.7h-13.4c-5.5 0-8.8 3.4-8.8 8 0 4.8 3.1 7.6 7.9 7.6h6c1.3 0 2.3.8 2.3 2.1s-1 2.1-2.3 2.1h-13.2v5.7h13.1c5.5 0 8.8-3.3 8.8-8.3 0-4.6-2.9-7.3-7.9-7.3h-6c-1.3 0-2.3-.8-2.3-2.1zM150 104.1h20.7v-5.7h-14.3v-4.3h13.9v-5.6h-13.9v-4.1h14.3v-5.7H150v25.4zm-1 0l-8.2-9.3c4.2-.9 6.7-4 6.7-8 0-4.9-3.4-8.2-8.8-8.2h-14.1V104h6.5v-9h2.5l7.6 9h7.8v.1zm-11.1-14.5h-7v-5.2h7c2 0 3 1.2 3 2.6 0 1.5-1 2.6-3 2.6zm-16.2-2.3c0-5.1-3.4-8.6-8.8-8.6H98.6v25.4h6.5v-8.3h7.7c5.4.1 8.9-3.6 8.9-8.5zm-6.5 0c0 1.6-1 2.8-3 2.8H105v-5.7h7.2c2 0 3 1.3 3 2.9zM98 104.1L87.8 91.3 98 78.7h-8l-6 7.8-6-7.8h-8.1l10.2 12.8L70 104.1h7.9l6-7.9 6 7.9H98zm-49.3 0h20.7v-5.7H55.2v-4.3h13.9v-5.6H55.2v-4.1h14.3v-5.7H48.7v25.4z" />
                                                            <path
                                                                fill="#FFF"
                                                                d="M220.8 0H0v141.8h220.8M218.9 139.9H2.1V1.9h216.8v138z"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <span id="semafoneResponseSpan" className="semafoneNoResponse"></span>
                                        </div>

                                        <div id="semaLoadingMsg"></div>
                                        <div className="semafoneCCNumber">
                                            <input
                                                type="text"
                                                id="isPanFocusedOnInitialise"
                                                defaultValue="false"
                                                style={{ display: 'none' }}
                                            />
                                            <input
                                                type="text"
                                                id="isPanResetOnInitialFocus"
                                                defaultValue="true"
                                                style={{ display: 'none' }}
                                            />
                                            <input type="hidden" id="cardScheme" defaultValue="" />
                                            <input type="hidden" id="maskedCardNumber" defaultValue="" />
                                            <input type="hidden" id="token" defaultValue="" />
                                            <input type="hidden" id="semafoneResponse" defaultValue="" />
                                            <input
                                                type="text"
                                                id="completeSemafoneResponse"
                                                value={completeSemafoneResp}
                                                onChange={onNonTscCreditCardAdded}
                                                style={{ display: 'none' }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <span>
                                            <h3 className="semafone__expiration-title">{t('Expiration Date')}</h3>
                                        </span>
                                        <div className="creditcard__expirey-wrap">
                                            <MaskedInput
                                                mask={[/[0-9]/, /\d/]}
                                                id="creditCardMonthId"
                                                value={creditCardExpiryMonth}
                                                guide={false}
                                                type="tel"
                                                onFocus={(e) => (e.target.placeholder = '')}
                                                onBlur={(e) => (e.target.placeholder = 'MM')}
                                                name="expiryMonth"
                                                placeholder="MM"
                                                className="creditcard__expirey"
                                                onChange={(e) => {
                                                    if (e.target.value.length > 1) {
                                                        //need to research how set focus
                                                        //@ts-ignore
                                                        creditCardRef.current.inputElement.focus();
                                                    }
                                                    setCreditCardExpiryMonth(e.target.value);
                                                }}
                                            />
                                            <span className="semafone__expiration-title"> / </span>
                                            <MaskedInput
                                                mask={[/[1-9]/, /\d/]}
                                                id="creditCardYearId"
                                                value={creditCardExpiryYear}
                                                guide={false}
                                                type="tel"
                                                ref={creditCardRef}
                                                onFocus={(e) => (e.target.placeholder = '')}
                                                onBlur={(e) => (e.target.placeholder = 'YY')}
                                                name="expiryYear"
                                                placeholder={t('YY')}
                                                className="creditcard__expirey"
                                                onChange={(e) => setCreditCardExpiryYear(e.target.value)}
                                            />
                                        </div>

                                        {cvvEnabled && (
                                            <>
                                                <span>
                                                    <h3 className="semafone__cvv-title">
                                                        {t('CVV')}
                                                        <Popover placement="right">
                                                            <PopoverTrigger className="popover__button">
                                                                <span className="visually-hidden">
                                                                    {t('CVV tooltip')}
                                                                </span>
                                                                <InfoTooltipSvg className="popover__tooltip" />
                                                            </PopoverTrigger>
                                                            <PopoverContent className="popover__message">
                                                                {t(
                                                                    '3 digit number on the back of your VISA or MASTERCARD ',
                                                                )}
                                                                <br />
                                                                {t(
                                                                    '4 digit number on the front of your American Express Card',
                                                                )}
                                                                <PopoverClose />
                                                                <PopoverArrow className="popover__arrow" />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </h3>
                                                </span>
                                                <div className="creditcard__cvv-wrap">
                                                    <input
                                                        id="creditCardCvvId"
                                                        value={pagePopupLink}
                                                        type="tel"
                                                        name="cardCvv"
                                                        placeholder=""
                                                        autoComplete="off"
                                                        required
                                                        aria-label="Credit Card CVV"
                                                        style={{ width: 68 }}
                                                        maxLength={4}
                                                        // eslint-disable-next-line jsx-a11y/no-autofocus
                                                        autoFocus
                                                        inputMode="numeric"
                                                        className="creditcard__cvv"
                                                        onFocus={() => {
                                                            setCreditCardErrorMessage('');
                                                        }}
                                                        onChange={(e) => {
                                                            // Only allow numbers
                                                            const value = e.target.value.replace(/\D/g, '');
                                                            setPagePopupLink(value);
                                                        }}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {showPayPal && (
                                <div className="creditcard__selector--cc selector__paypal" key="paypal">
                                    <input
                                        name="paymentMethodRad"
                                        type="radio"
                                        id="tscPaypal"
                                        key="paypal"
                                        data-link-type="radio button"
                                        data-link-title="paypal"
                                        data-link-position="card type"
                                        data-component-type="express checkout"
                                        checked={paymentCardType == TmCreditCardSelectionType.PayPal}
                                        onChange={() => onPayPalButtonClicked()}
                                        value="paypal"
                                        style={{ display: 'none' }}
                                    />
                                    <label className="card__label" key="paypalLabel" htmlFor="tscPaypal">
                                        <svg
                                            className="card__radio--selected"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 541.6 541.6"
                                        >
                                            <path
                                                data-name="svg 1"
                                                d="M270.8 0a270.9 270.9 0 1 0 .2 541.8A270.9 270.9 0 0 0 270.8 0zm0 522.5c-138.7 0-251.7-113-251.7-251.7s113-251.7 251.7-251.7 251.7 113 251.7 251.7-113 251.7-251.7 251.7z"
                                            />
                                            <circle cx="270.8" cy="270.8" r="108.3" />
                                        </svg>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 541.6 541.6"
                                            className="card__radio--unselected"
                                        >
                                            <path d="M270.8 0C121.3 0 0 121.3 0 270.8s121.3 270.8 270.8 270.8 270.8-121.3 270.8-270.8S420.3 0 270.8 0zm0 522.5c-138.7 0-251.7-113-251.7-251.7s113-251.7 251.7-251.7 251.7 113 251.7 251.7-113 251.7-251.7 251.7z" />
                                        </svg>
                                        <div style={{ display: 'inline-block' }}>
                                            <img
                                                src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
                                                alt="PayPal"
                                            />
                                            {loaded && paypalSelected && (
                                                <span ref={paypalRef} className="paypalButtonWrap"></span>
                                            )}
                                        </div>
                                        <div>
                                            {loaded && paypalSelected && showPayPalButtonNotClickedMsg && (
                                                <div className="creditcard__error-wrap">
                                                    <>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            className="form__error-svg"
                                                        >
                                                            <path d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                                        </svg>
                                                        {t('Please click or tap on the PayPal button to proceed.')}
                                                    </>
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
